import axiosInstance from "utils/axiosInstance";

const attendanceApi = {
  /**
   * Fetches an employee by ID
   * @param {string} employeeId - The ID of the employee to fetch
   * @returns {Promise<Object>} A promise that resolves to the employee object
   */
  getEmployee: async (employeeId) => {
    const response = await axiosInstance.get(`/employee/me/${employeeId}`);
    return response?.data?.data?.employee || {};
  },

  /**
   * Fetches attendance statistics for an employee
   * @param {string} employeeId - The ID of the employee
   * @param {string} year - The year
   * @param {string} month - The month
   * @returns {Promise<Object>} A promise that resolves to the attendance statistics
   */
  getAttendanceStats: async (employeeId, year, month) => {
    const response = await axiosInstance.get(`/employee/me/attendance-stats/${employeeId}?year=${year}&month=${month}`);
    return response?.data?.data || {};
  },

  /**
   * Fetches attendance history for an employee
   * @param {string} employeeId - The ID of the employee
   * @param {string} year - The year
   * @param {string} month - The month
   * @param {number} page - The page number
   * @param {number} limit - The number of records per page
   * @returns {Promise<Array>} A promise that resolves to the attendance history
   */
  getAttendanceHistory: async (employeeId, year, month, page = 1, limit = 100) => {
    const response = await axiosInstance.get(`/employee/me/attendance-history/${employeeId}?year=${year}&month=${month}&page=${page}&limit=${limit}`);
    return response?.data?.data?.attendanceRecords || [];
  },

  /**
   * Exports attendance data for an employee
   * @param {string} employeeId - The ID of the employee
   * @param {string} year - The year
   * @param {string} month - The month
   * @returns {Promise<Blob>} A promise that resolves to the CSV data as a Blob
   */
  exportAttendanceData: async (employeeId, year, month) => {
    const response = await axiosInstance.get(`/employee/me/attendance-export/${employeeId}?year=${year}&month=${month}`, {
      responseType: 'blob'
    });
    return response.data;
  },
  exportAllEmployees: async () => {
    const response = await axiosInstance.get("employee/get/export/csv", {
      responseType: "blob",
    });
    return response.data;
  },
  exportEmployeeAttendanceData: async (employeeId, filter) => {
    const response = await axiosInstance.get(
      `employee/getbyid/${employeeId}?filter=${filter}`,
      { responseType: 'blob' }
    );
    return response.data;
  },
  getEmployeeAttendanceRecords: async (employeeId, filter) => {
    const response = await axiosInstance.get(
      `employee/getbyid/${employeeId}?filter=${filter}`
    );
    return response?.data?.records?.map((r) => ({
      ...r,
      checkIn: r.checkIn ? new Date(r.checkIn) : null,
      checkOut: r.checkOut ? new Date(r.checkOut) : null,
    })) || [];
  },
  getEmployeeAttendanceSummary: async (employeeId) => {
    const response = await axiosInstance.get(
      `employee/get-summary/${employeeId}`
    );
    return response.data;
  },
  deleteAttendanceRecord: async (recordId) => {
    const response = await axiosInstance.delete(
      `attendance/delete/${recordId}`
    );
    return response.data;
  },
  updateAttendanceRecord: async (recordId, data) => {
    const response = await axiosInstance.put(
      `attendance/update/${recordId}`,
      data
    );
    return response.data;
  },
  getAttendanceRecords: async () => {
    const response = await axiosInstance.get("attendance/getall");
    return response?.data || {};
  },
  createAttendanceMaster: async (payload) => {
    const response = await axiosInstance.post("/attendance/master/create", payload)
    return response?.data || {};
  },

  getAttendanceMasterByEmployee: async (employeeId, companyId) => {
    const response = await axiosInstance.get(`/attendance/master/get/employee/${employeeId}?company_id=${companyId}`);
    return response?.data || {};
  },

  /**
   * Fetches attendance master data for a date range
   * @param {string} employeeId - The ID of the employee
   * @param {number} companyId - The ID of the company
   * @param {string} startDate - The start date in YYYY-MM-DD format
   * @param {string} endDate - The end date in YYYY-MM-DD format
   * @returns {Promise<Object>} A promise that resolves to the attendance master data
   */
  getAttendanceMasterByDateRange: async (employeeId, companyId, startDate, endDate) => {
    const response = await axiosInstance.get(
      `/attendance/master/get/employee/${employeeId}/daterange?company_id=${companyId}&start_date=${startDate}&end_date=${endDate}`
    );
    return response?.data || {};
  },

  // TRANSACTION (Monthly)
  createAttendanceTransaction: async (payload) => {
    const response = await axiosInstance.post("/attendance/transaction/create", payload)
    return response?.data || {};
  },

  getAttendanceTransaction: async (employeeId, month, year, companyId) => {
    const response = await axiosInstance.get(`/attendance/transaction/get/employee/${employeeId}/${month}/${year}?company_id=${companyId}`);
    return response?.data || {};
  },
  getEmployeesAttendanceSummary: async (month, year) => {
    const response = await axiosInstance.get(
      `/attendance/employees/summary?month=${month}&year=${year}`
    );
    return response?.data?.data?.employees || [];
  },
};

export default attendanceApi;